import { TimeoutError } from '@/app/sync/github';
import type {
  OnProgress,
  Progress,
  SyncResult,
} from '@/app/sync/sync';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export type SyncStatus =
  | { state: 'idle', lastSynced: Date | null }
  // null until the sync knows of notes to push or pull
  | { state: 'syncing', progress: Progress | null }
  // stalled: timed out more than once in a row - the connection, not a blip
  | { state: 'failed', error: string, stalled: boolean };

export type RunSync = (onProgress: OnProgress) => Promise<SyncResult>;

const AFTER_EDIT_MS = 3000;
const EVERY_MS = 60_000;
// A sync that timed out is tried again later and later rather than a minute
// apart: an upload that stalled leaves its bytes queued on the browser's
// connection, ahead of every later request, and each retry adds its own.
// Asked for by hand, or on coming back online or into view, it runs at once.
const HOLD_MS = [60_000, 120_000, 240_000, 480_000];

// Syncs when the app opens, when it comes back into view or online, a
// minute apart while it is open, and a few seconds after an edit. One run at
// a time; a request during a run gets one more run after it.
export const useSync = (
  sync: RunSync,
  { beforeSync, afterSync }: {
    beforeSync: () => Promise<void>,
    afterSync: (result: SyncResult) => Promise<void>,
  },
) => {
  const [status, setStatus] = useState<SyncStatus>({
    state: 'idle',
    lastSynced: null,
  });
  const running = useRef(false);
  const again = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const timeouts = useRef(0);
  const holdUntil = useRef(0);
  // the callbacks close over component state; keep the latest
  const hooks = useRef({ beforeSync, afterSync });
  hooks.current = { beforeSync, afterSync };

  const syncNow = useCallback(async () => {
    clearTimeout(timer.current);
    if (running.current) {
      again.current = true;
      return;
    }
    running.current = true;
    try {
      do {
        again.current = false;
        setStatus({ state: 'syncing', progress: null });
        await hooks.current.beforeSync();
        await hooks.current.afterSync(
          await sync(progress => setStatus({ state: 'syncing', progress })),
        );
      } while (again.current);
      timeouts.current = 0;
      holdUntil.current = 0;
      setStatus({ state: 'idle', lastSynced: new Date() });
    } catch (e) {
      console.error('sync failed', e);
      timeouts.current = e instanceof TimeoutError ? timeouts.current + 1 : 0;
      holdUntil.current = timeouts.current
        ? Date.now()
          + HOLD_MS[Math.min(timeouts.current, HOLD_MS.length) - 1]
        : 0;
      setStatus({
        state: 'failed',
        error: e instanceof Error ? e.message || e.name : String(e),
        stalled: timeouts.current > 1,
      });
    } finally {
      running.current = false;
    }
  }, [sync]);

  const unlessHeld = useCallback(() => {
    if (Date.now() >= holdUntil.current) syncNow();
  }, [syncNow]);

  const syncSoon = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(unlessHeld, AFTER_EDIT_MS);
  }, [unlessHeld]);

  useEffect(() => {
    syncNow();
    const onVisible = () => {
      if (document.visibilityState === 'visible') syncNow();
    };
    const interval = setInterval(unlessHeld, EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', syncNow);
    return () => {
      clearInterval(interval);
      clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', syncNow);
    };
  }, [syncNow, unlessHeld]);

  return { status, syncNow, syncSoon };
};
